import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Download, FileText, ArrowLeft, AlertCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function ConfirmationPage() {
  const { declarationId: urlDeclarationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [declaration, setDeclaration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  // Get declaration ID from multiple sources (priority order)
  const getDeclarationId = () => {
    // 1. URL params (e.g., /confirmation/abc123)
    if (urlDeclarationId) return urlDeclarationId;
    
    // 2. Query params (e.g., /confirmation?declarationId=abc123)
    const queryId = searchParams.get("declarationId");
    if (queryId) return queryId;
    
    // 3. localStorage
    try {
      const storedId = localStorage.getItem("cfe_declaration_id");
      if (storedId) return storedId;
    } catch (e) {
      console.warn("localStorage not available:", e);
    }
    
    return null;
  };

  const effectiveDeclarationId = getDeclarationId();

  useEffect(() => {
    const fetchDeclaration = async () => {
      if (!effectiveDeclarationId) {
        setLoading(false);
        return;
      }

      try {
        // Try to mark as paid (ignore errors - might already be paid)
        try {
          await fetch(`${API}/declarations/${effectiveDeclarationId}/mark-paid`, {
            method: "POST",
            headers: { "Content-Type": "application/json" }
          });
        } catch (e) {
          console.warn("Could not mark as paid:", e);
        }
        
        // Fetch declaration data (optional - for display only)
        try {
          const response = await fetch(`${API}/declarations/${effectiveDeclarationId}`);
          if (response.ok) {
            const data = await response.json();
            setDeclaration(data);
          }
        } catch (e) {
          console.warn("Could not fetch declaration details:", e);
        }
      } catch (err) {
        console.error("Error in fetchDeclaration:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDeclaration();
  }, [effectiveDeclarationId]);

  const handleDownload = async () => {
    if (!effectiveDeclarationId) {
      setError("Impossible de télécharger : aucune déclaration trouvée.");
      return;
    }

    setDownloading(true);
    setError(null);

    try {
      // Open download URL in new window
      const downloadUrl = `${API}/declarations/${effectiveDeclarationId}/download`;
      window.open(downloadUrl, "_blank");
      setDownloadSuccess(true);
    } catch (err) {
      console.error("Download error:", err);
      setError("Erreur lors du téléchargement. Veuillez réessayer.");
    } finally {
      setDownloading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#2563EB] animate-spin mx-auto mb-4" />
          <p className="text-[#475569]">Chargement de votre document...</p>
        </div>
      </div>
    );
  }

  // No declaration ID found
  if (!effectiveDeclarationId) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        {/* Navigation */}
        <nav className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <a href="/" className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-[#2563EB]" />
                <span className="font-bold text-lg text-[#0F172A]">CFE Auto-Entrepreneur</span>
              </a>
            </div>
          </div>
        </nav>

        {/* Error Content */}
        <main className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mx-auto">
            <Card className="bg-white border-slate-200 shadow-xl">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="bg-amber-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="h-10 w-10 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Déclaration introuvable</h2>
                <p className="text-[#475569] mb-6">
                  Impossible de retrouver votre déclaration. Cela peut arriver si vous avez vidé les données de votre navigateur 
                  ou si vous accédez à cette page depuis un autre appareil.
                </p>
                <p className="text-[#475569] mb-8">
                  Veuillez retourner au formulaire et générer votre PDF à nouveau.
                </p>
                <Button 
                  onClick={() => navigate("/")}
                  className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white h-12 px-6"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Revenir au formulaire
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 px-4 text-center text-sm text-[#94A3B8] border-t border-slate-200 mt-auto">
          <p>© {new Date().getFullYear()} CFE Auto-Entrepreneur. Tous droits réservés.</p>
        </footer>
      </div>
    );
  }

  // Success state - Declaration found
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-[#2563EB]" />
              <span className="font-bold text-lg text-[#0F172A]">CFE Auto-Entrepreneur</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto">
          <Card className="bg-white border-slate-200 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="bg-green-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <CardTitle className="text-2xl sm:text-3xl text-[#0F172A]">Paiement confirmé</CardTitle>
              <p className="text-[#475569] mt-2">
                Merci, votre paiement a bien été pris en compte.
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Declaration Summary (optional) */}
              {declaration && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200">
                  <h3 className="font-semibold text-[#0F172A] mb-3">Récapitulatif</h3>
                  <div className="space-y-2 text-sm">
                    {declaration.denomination && (
                      <div className="flex justify-between">
                        <span className="text-[#475569]">Déclarant</span>
                        <span className="font-medium text-[#0F172A]">{declaration.denomination}</span>
                      </div>
                    )}
                    {declaration.siret && (
                      <div className="flex justify-between">
                        <span className="text-[#475569]">SIRET</span>
                        <span className="font-mono text-[#0F172A]">{declaration.siret}</span>
                      </div>
                    )}
                    {declaration.annee_declaration && (
                      <div className="flex justify-between">
                        <span className="text-[#475569]">Année</span>
                        <span className="text-[#0F172A]">{declaration.annee_declaration}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 text-center">{error}</p>
                </div>
              )}

              {/* Download Button */}
              <Button
                onClick={handleDownload}
                disabled={downloading}
                size="lg"
                className="w-full h-14 bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-lg"
              >
                {downloading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Téléchargement...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-5 w-5" />
                    Télécharger mon PDF
                  </>
                )}
              </Button>

              {/* Success message after download */}
              {downloadSuccess && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 text-center">
                    Le téléchargement a démarré. Vérifiez votre dossier de téléchargements.
                  </p>
                </div>
              )}

              {/* Instructions */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <h4 className="font-medium text-[#0F172A] mb-2">Prochaines étapes</h4>
                <ol className="text-sm text-[#475569] space-y-2 list-decimal list-inside">
                  <li>Téléchargez et imprimez votre déclaration</li>
                  <li>Signez le document dans le cadre prévu</li>
                  <li>Envoyez-le à votre Service des Impôts des Entreprises (SIE)</li>
                </ol>
              </div>

              {/* Reference */}
              <div className="mt-6 text-center text-sm text-[#94A3B8]">
                <p>Référence de votre déclaration :</p>
                <p className="font-mono text-[#475569] mt-1 break-all">{effectiveDeclarationId}</p>
              </div>

              {/* Back Home */}
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full mt-4 text-[#475569]"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à l&apos;accueil
              </Button>

              {/* Section complémentaire - Autres documents */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h4 className="text-sm font-medium text-[#475569] mb-2 text-center">
                  Autres documents administratifs à générer ?
                </h4>
                <p className="text-xs text-[#94A3B8] text-center mb-2">
                  Pour créer facilement vos documents PDF administratifs (attestations, formulaires simples, documents officiels), découvrez notre outil en ligne.
                </p>
                <a 
                  href="https://pdf-facile.fr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block text-center text-sm text-[#2563EB] hover:text-[#1d4ed8] transition-colors"
                >
                  👉 Découvrir PDF Facile
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-sm text-[#94A3B8] border-t border-slate-200">
        <p>© {new Date().getFullYear()} CFE Auto-Entrepreneur. Tous droits réservés.</p>
        <div className="mt-2 space-x-4">
          <a href="/mentions-legales" className="hover:text-[#2563EB]">Mentions légales</a>
          <a href="/cgv" className="hover:text-[#2563EB]">CGV</a>
        </div>
      </footer>
    </div>
  );
}
